/**
 * Input — text input with optional leading + trailing icons or actions.
 *
 * Props:
 *   leadingIcon    — icon component placed at the start (logical, RTL-aware)
 *   trailingIcon   — icon component placed at the end
 *   trailingAction — clickable element placed at the end (overrides trailingIcon)
 *   invalid        — show error styling
 *   size           — sm (h-8) / md (h-10, default) / lg (h-12)
 *   tone           — 'default' (solid border) / 'glass' (for auth pages)
 *
 * Inputs use logical padding (ps-/pe-) so icons stay on the correct side
 * under RTL.
 */

import { forwardRef } from 'react'

const SIZES = {
  sm: 'h-8  text-xs',
  md: 'h-10 text-sm',
  lg: 'h-12 text-base',
}

const TONES = {
  default: 'bg-surface text-ink-primary border border-border placeholder:text-ink-muted hover:border-border-strong focus:border-brand focus:ring-brand/30',
  // Glass tone: nearly opaque white card-style input matching the Figma —
  // pops cleanly against the pastel gradient. Dark mode keeps it readable.
  glass:   'bg-white/95 dark:bg-white/10 text-ink-primary border border-white/70 dark:border-white/15 placeholder:text-ink-muted backdrop-blur focus:border-brand focus:ring-brand/30 shadow-token-sm',
}

const Input = forwardRef(function Input(
  {
    leadingIcon: Leading,
    trailingIcon: Trailing,
    trailingAction,
    invalid = false,
    size = 'md',
    tone = 'default',
    className = '',
    type = 'text',
    ...rest
  },
  ref
) {
  const sizeCls = SIZES[size] || SIZES.md
  const toneCls = TONES[tone] || TONES.default
  const errorCls = invalid ? 'border-danger focus:border-danger focus:ring-danger/30' : ''
  const padStart = Leading ? 'ps-9' : 'ps-3'
  const padEnd = (Trailing || trailingAction) ? 'pe-9' : 'pe-3'

  return (
    <div className="relative">
      {Leading && (
        <Leading
          className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted pointer-events-none"
          aria-hidden="true"
        />
      )}
      <input
        ref={ref}
        type={type}
        className={`
          w-full rounded-lg transition-colors
          focus:outline-none focus:ring-2
          ${sizeCls} ${toneCls} ${padStart} ${padEnd} ${errorCls} ${className}
        `}
        aria-invalid={invalid || undefined}
        {...rest}
      />
      {trailingAction ? (
        <span className="absolute end-1 top-1/2 -translate-y-1/2">
          {trailingAction}
        </span>
      ) : Trailing && (
        <Trailing
          className="absolute end-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted pointer-events-none"
          aria-hidden="true"
        />
      )}
    </div>
  )
})

export default Input
